import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { CreditCard, Eye, RefreshCw, X } from "lucide-react";

// Read-only mirror of the client's /client/payments portal, rendered for an
// admin from /cashflow ("view as client"). No impersonation: the admin stays
// logged in, the backend computes the exact same payload the client would see
// via /admin/client-payment-schedule. Visual markup intentionally mirrors
// MyPaymentSchedulePage's debt hero + outstanding table so a screenshot of this
// modal == what the client sees. Interactive bits (checkboxes, Stripe pay) are
// dropped — this is for eyeballing + screenshotting, not acting.

const SYM = { USD: "$", EUR: "€", UAH: "₴", GBP: "£" };
const fmtMoney = (amount, currency = "USD") => {
  const sym = SYM[currency] || `${currency} `;
  const n = Math.round((amount || 0) * 100) / 100;
  return `${sym}${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};
const fmtUsd = (n) => `$${Math.round(n || 0).toLocaleString("en-US")}`;
const fmtUsdCents = (n) =>
  `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const LivePenaltyCounter = ({ base, perDay }) => {
  const [drift, setDrift] = useState(0);
  useEffect(() => {
    if (!perDay) return undefined;
    const startedAt = Date.now();
    const t = setInterval(() => {
      setDrift(((Date.now() - startedAt) / 1000) * (perDay / 86400));
    }, 1000);
    return () => clearInterval(t);
  }, [perDay]);
  return <span className="tabular-nums">{fmtUsdCents(base + drift)}</span>;
};

const StatusBadge = ({ row }) => {
  if (row.days_overdue != null) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 text-[11px] font-mono rounded border bg-red-500/10 text-red-300 border-red-500/30">
        Late +{row.days_overdue}d
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 text-[11px] font-mono rounded border bg-amber-500/10 text-amber-300 border-amber-500/30">
      Due in {row.days_left ?? "—"}d
    </span>
  );
};

const ClientDebtPreview = ({ clientId, clientName, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get("/admin/client-payment-schedule", {
        params: { client_id: clientId },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const outstanding = useMemo(
    () => [...(data?.buckets?.overdue || []), ...(data?.buckets?.pending || [])],
    [data]
  );
  const penalty_settings = data?.penalty_settings;
  const summary = data?.summary;

  const debt = useMemo(() => {
    const overdueRows = data?.buckets?.overdue || [];
    let perDay = 0;
    let accrued = 0;
    let inGrace = 0;
    let maxLate = 0;
    for (const r of overdueRows) {
      maxLate = Math.max(maxLate, r.days_overdue || 0);
      const p = r.penalty;
      if (p && !r.penalty_paid_at) {
        accrued += p.amount || 0;
        perDay += ((p.base_amount || 0) * (p.rate_per_day || 0)) / 100;
      } else if (!p) {
        inGrace += 1;
      }
    }
    return { perDay, accrued, inGrace, maxLate };
  }, [data]);

  const resolvedName = data?.viewing_as?.client_name || clientName || "client";

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl rounded-lg border border-zinc-700 bg-zinc-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal chrome — admin context, not part of the client view. */}
        <div className="flex items-center justify-between gap-3 border-b border-zinc-800 px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <Eye className="h-4 w-4 text-orange-400 shrink-0" />
            <span className="text-sm text-zinc-400 shrink-0">Viewing as</span>
            <span className="text-sm font-semibold text-zinc-100 truncate">
              {resolvedName}
            </span>
            <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded border border-zinc-700 text-zinc-500">
              read-only
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={load}
              className="h-7 px-2 text-xs text-zinc-400 gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              className="h-7 w-7 p-0 text-zinc-400"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* This is what the client sees on /client/payments. */}
        <div className="p-4 sm:p-5 space-y-4">
          <div className="text-[11px] text-zinc-500">
            Exactly what {resolvedName} sees on their <span className="font-mono">Payments due</span> page.
          </div>

          {loading && <div className="py-10 text-center text-zinc-500">Loading…</div>}
          {error && (
            <div className="rounded border border-red-500/30 bg-red-950/40 p-3 text-sm text-red-300">
              {error}
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Summary strip */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm border border-zinc-800 bg-zinc-900/40 rounded-md px-4 py-3">
                <div>
                  <span className="text-zinc-500 mr-1.5">Outstanding</span>
                  <span className="font-mono font-semibold text-zinc-100">
                    {fmtUsd((summary?.overdue_total || 0) + (summary?.pending_total || 0))}
                  </span>
                  <span className="text-zinc-500 ml-1.5 text-xs">
                    ({(summary?.overdue_count || 0) + (summary?.pending_count || 0)})
                  </span>
                </div>
                {summary?.overdue_count > 0 && (
                  <div>
                    <span className="text-red-400 mr-1.5">Overdue</span>
                    <span className="font-mono font-semibold text-red-300">
                      {fmtUsd(summary.overdue_total)}
                    </span>
                    <span className="text-zinc-500 ml-1.5 text-xs">({summary.overdue_count})</span>
                  </div>
                )}
                <div>
                  <span className="text-zinc-500 mr-1.5">Paid</span>
                  <span className="font-mono font-semibold text-emerald-300">
                    {fmtUsd(summary?.received_60d_total)}
                  </span>
                </div>
              </div>

              {/* Debt hero — mirrors MyPaymentSchedulePage. */}
              {penalty_settings?.enabled && summary?.overdue_count > 0 && (
                <div className="relative overflow-hidden rounded-lg border border-red-500/30 bg-gradient-to-br from-red-950/50 via-zinc-900/90 to-zinc-900">
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-500/70 to-transparent" />
                  <div className="flex flex-col sm:flex-row sm:items-stretch sm:divide-x divide-y sm:divide-y-0 divide-zinc-800/70">
                    <div className="px-5 py-4 flex-1 min-w-[170px]">
                      <div className="text-[11px] uppercase tracking-widest text-zinc-500">
                        Overdue balance
                      </div>
                      <div className="mt-1 text-3xl font-mono font-bold text-zinc-100">
                        {fmtUsd(summary.overdue_total)}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {summary.overdue_count} invoice{summary.overdue_count === 1 ? "" : "s"} · oldest{" "}
                        {debt.maxLate}d late
                      </div>
                    </div>
                    <div className="px-5 py-4 flex-1 min-w-[230px]">
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-red-400/90">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                        </span>
                        Late penalty accruing
                      </div>
                      <div className="mt-1 text-3xl font-mono font-bold text-red-300">
                        <LivePenaltyCounter base={debt.accrued} perDay={debt.perDay} />
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        <span className="text-red-300/90 font-mono font-semibold">
                          +{fmtUsd(debt.perDay)}
                        </span>{" "}
                        every day · {penalty_settings.default_rate_per_day}%/day of each unpaid invoice
                        {debt.inGrace > 0 && (
                          <span className="block text-zinc-600">
                            {debt.inGrace} more invoice{debt.inGrace === 1 ? "" : "s"} start
                            {debt.inGrace === 1 ? "s" : ""} accruing after the{" "}
                            {penalty_settings.default_grace_period_days}-day grace period
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="px-5 py-4 flex flex-col justify-center items-start sm:items-end gap-2 sm:min-w-[210px]">
                      <div className="text-xs text-zinc-400 sm:text-right">
                        Settle today and the counter stops.
                        <span className="block text-zinc-600">
                          By tomorrow: {fmtUsd(debt.accrued + debt.perDay)} penalty
                        </span>
                      </div>
                      <Button
                        size="sm"
                        disabled
                        className="h-8 bg-red-500/80 text-zinc-50 gap-2 cursor-default"
                      >
                        <CreditCard className="h-3.5 w-3.5" />
                        Settle overdue
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Outstanding table */}
              {outstanding.length === 0 ? (
                <div className="rounded border border-zinc-800 bg-zinc-900/40 p-8 text-center text-sm text-zinc-500">
                  No outstanding invoices for this client.
                </div>
              ) : (
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="w-24">Status</TableHead>
                        <TableHead>Invoice</TableHead>
                        <TableHead className="hidden md:table-cell">Project</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="hidden sm:table-cell">Due date</TableHead>
                        <TableHead className="text-right">Penalty</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {outstanding.map((row) => {
                        const hasPenalty = (row.penalty?.amount || 0) > 0;
                        return (
                          <TableRow key={row.id} className="border-zinc-800">
                            <TableCell>
                              <StatusBadge row={row} />
                            </TableCell>
                            <TableCell className="font-medium text-zinc-200">{row.name}</TableCell>
                            <TableCell className="hidden md:table-cell text-zinc-400 text-sm">
                              {row.project_name || "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono text-zinc-100">
                              {fmtMoney(row.amount, row.currency)}
                              {row.currency !== "USD" && (
                                <div className="text-[10px] text-zinc-500">
                                  ≈ {fmtUsd(row.amount_usd)}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="hidden sm:table-cell text-zinc-400 text-sm font-mono">
                              {row.invoice_due_date || "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              {hasPenalty ? (
                                <div className="flex flex-col items-end leading-tight">
                                  <span className="font-mono text-sm font-semibold text-red-300">
                                    {fmtUsd(row.penalty.amount)}
                                  </span>
                                  <span className="text-[10px] text-zinc-500 font-mono">
                                    {row.penalty.rate_per_day}%/d · {row.penalty.days_charged}d
                                  </span>
                                </div>
                              ) : (
                                <span className="text-zinc-600 text-sm">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDebtPreview;
